import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ContentLayout, PageLayout } from '../../layout';
import { MenuTitles, Pages } from '../../constants/menu';
import { useQueryClient } from '@tanstack/react-query';
import { AgxToastState } from 'types/commonTypes';
import {
  AgxColumn,
  AgxDivider,
  AgxRow,
  AgxButton,
  AgxTypography,
  DropdownOption,
  getDateString,
} from '@urbanx/agx.ui-components2';
import { AgxToast } from '@urbanx/agx-ui-components';
import './partnership.scss';
import { FormPrompt } from 'components/FormPrompt';
import { isEqual } from 'lodash-es';
import ContentLoader from 'react-content-loader';
import { AuthContext } from '@urbanx/agx.auth.client';
import {
  PartnershipDetails,
  PartnershipTierLevel,
  useStaffSetPartnershipDetailsForAgency,
  useInvalidateUseGetAllAgencies,
  useInvalidateUseGetAgencyDetails,
  PartnershipDetailsPartnershipLevel,
} from '@urbanx/agx.agencies.client';
import { PartnershipLevelOptions } from './PartnershipLevelOptions';
import { useSelectedAgency } from 'hooks';
import { defaultPartnershipTemplates } from './defaultPartnershipTemplates';
import { tierOrder } from './tierOrder';
import { buildState } from './buildState';
import PartnershipInformation from './partershipInformation';
import PartnershipAgreement from './partnershipAgreement';

const PartnershipPage = () => {
  const { agencyId } = useParams();
  const queryClient = useQueryClient();
  const { userAccount } = useContext(AuthContext);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const partnershipDetailsKey = [`partnershipDetails-${agencyId}`, agencyId];
  const invalidateAgencies = useInvalidateUseGetAllAgencies();
  const invalidateGetAgencyDetails = useInvalidateUseGetAgencyDetails();

  const { agency } = useSelectedAgency();
  const partnershipDetails: PartnershipDetails | undefined = agency?.agency?.partnershipDetails;

  const {
    staffSetPartnershipDetailsForAgency: updatePartnershipDetails,
    staffSetPartnershipDetailsForAgencyPending,
  } = useStaffSetPartnershipDetailsForAgency({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: partnershipDetailsKey });
      updateToastState({
        color: 'success',
        message: 'Partnership details updated successfully',
        open: true,
      });
    },
    onError: (error) => {
      console.error('Error updating partnership details:', error);
      updateToastState({
        color: 'error',
        message: 'Error updating partnership details',
        open: true,
      });
    },
  });

  const [toastState, updateToastState] = useState<AgxToastState>({
    color: 'success',
    message: '',
    open: false,
  });

  useEffect(() => {
    if (staffSetPartnershipDetailsForAgencyPending) {
      updateToastState({
        color: 'info',
        message: 'Saving partnership details...',
        open: true,
      });
    } else {
      updateToastState((prev) => ({ ...prev, open: false }));
    }
  }, [staffSetPartnershipDetailsForAgencyPending]);

  const [partnershipDetailsState, setPartnershipDetailsState] = useState<
    PartnershipDetails | undefined
  >(undefined);

  useEffect(() => {
    if (!agency?.agency?.partnershipDetails) return;
    const result = buildState(agency.agency.partnershipDetails);
    if (!isEqual(partnershipDetailsState, result)) {
      setPartnershipDetailsState(result);
    }
  }, [agency?.agency?.partnershipDetails]);

  const normalizeDetails = (details?: PartnershipDetails) => {
    if (!details) return undefined;

    const tiers =
      details.partnershipAgreement?.partnershipTiers?.map((t) => ({
        level: String(t.level),
        minimumGci: t.minimumGci ?? 0,
        maximumGci: t.maximumGci ?? 0,
        serviceFeePercentage: t.serviceFeePercentage ?? 0,
      })) ?? [];

    const normalizedDefaults = defaultPartnershipTemplates.map((t) => ({
      level: String(t.level),
      minimumGci: t.minimumGci ?? 0,
      maximumGci: t.maximumGci ?? 0,
      serviceFeePercentage: t.serviceFeePercentage ?? 0,
    }));

    const filteredTiers =
      details.partnershipAgreement?.customized === false
        ? tiers.filter((t, i) => JSON.stringify(t) !== JSON.stringify(normalizedDefaults[i]))
        : tiers;

    return {
      ...details,
      anniversaryDate: getDateString(details.anniversaryDate),
      partnershipAgreement: {
        customized: details.partnershipAgreement?.customized ?? false,
        partnershipTiers: filteredTiers,
      },
      partnershipLevel: details.partnershipLevel,
      targetLevelAdjustment: details.targetLevelAdjustment ?? 0,
    };
  };

  useEffect(() => {
    if (!partnershipDetailsState) return;
    const original = normalizeDetails(partnershipDetails);
    const current = normalizeDetails(partnershipDetailsState);
    setIsFormDirty(!isEqual(current, original));
  }, [partnershipDetailsState, partnershipDetails]);
  const partnershipLevel = partnershipDetailsState?.partnershipLevel;

  const inputLoader = (
    <ContentLoader viewBox="0 0 100 71" height="71px" width="30%">
      <rect x="0" y="0" rx="4" ry="4" width="50%" height="21px" />
      <rect x="0" y="26" rx="4" ry="4" width="100%" height="41px" />
    </ContentLoader>
  );

  const partnershipDefaultvalue = useMemo(() => {
    return (
      PartnershipLevelOptions.find(
        (plo) => plo.value === (partnershipDetails?.partnershipLevel ?? partnershipLevel)
      ) ?? PartnershipLevelOptions[0]
    );
  }, [partnershipLevel, partnershipDetails?.partnershipLevel]);

  const startingTier = useMemo(() => {
    return (
      partnershipDetailsState?.partnershipAgreement?.partnershipTiers
        ?.slice()
        .sort((a, b) => tierOrder[a.level!] - tierOrder[b.level!])[0]?.level ??
      PartnershipTierLevel.Bronze
    );
  }, [partnershipDetailsState]);

  const startingTierOption: DropdownOption = useMemo(
    () =>
      PartnershipLevelOptions.find((opt) => opt.value === startingTier) ??
      PartnershipLevelOptions[0],
    [startingTier]
  );

  const hasFormErrors = (): boolean => {
    if (typeof document === 'undefined') return false;
    return !!document.querySelector('.error');
  };

  const onDeleteItemHandler = useCallback(
    (levelToDelete?: PartnershipTierLevel) => {
      if (!levelToDelete) return;
      setPartnershipDetailsState((prev) => {
        const currentAgreement = prev?.partnershipAgreement;
        if (!currentAgreement) return prev;

        const currentTiers = currentAgreement.partnershipTiers ?? [];
        if (currentTiers.length <= 1) return prev;

        const newTiers = currentTiers.filter((t) => t.level !== levelToDelete);
        if (newTiers.length === 0) return prev;

        newTiers.sort((a, b) => tierOrder[a.level!] - tierOrder[b.level!]);
        const newStartingTier = newTiers[0]?.level;

        return {
          ...prev!,
          partnershipAgreement: {
            ...currentAgreement,
            partnershipTiers: newTiers,
          },
          partnershipLevel: newStartingTier as PartnershipDetailsPartnershipLevel | undefined,
        };
      });
    },
    [setPartnershipDetailsState]
  );

  const onAddTierHandler = useCallback(() => {
    setPartnershipDetailsState((prev) => {
      const currentAgreement = prev?.partnershipAgreement;
      if (!currentAgreement) return prev;

      const currentTiers = [...(currentAgreement.partnershipTiers ?? [])];
      const currentLevels = currentTiers.map((t) => t.level);
      const nextTierToAdd = defaultPartnershipTemplates.find(
        (t) => !currentLevels.includes(t.level)
      );
      if (!nextTierToAdd) return prev;

      const newTiers = [...currentTiers, { ...nextTierToAdd }];
      newTiers.sort((a, b) => tierOrder[a.level!] - tierOrder[b.level!]);

      return {
        ...prev!,
        partnershipAgreement: {
          ...currentAgreement,
          partnershipTiers: newTiers,
        },
        partnershipLevel: newTiers[0]?.level as PartnershipDetailsPartnershipLevel | undefined,
      };
    });
  }, [setPartnershipDetailsState]);

  if (!partnershipDetailsState) {
    return (
      <PageLayout
        agentName={userAccount?.firstName || ''}
        primaryPageTitle={agency?.name ?? ''}
        currentPageTitle={MenuTitles.PARTNERSHIP}
      >
        <ContentLayout secondaryActiveMenu={MenuTitles.PARTNERSHIP}>
          <AgxRow gap="spacing-3xl">
            {inputLoader}
            {inputLoader}
            {inputLoader}
          </AgxRow>
        </ContentLayout>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      agentName={userAccount?.firstName || ''}
      primaryPageTitle={agency?.name ?? ''}
      currentPageTitle={MenuTitles.PARTNERSHIP}
    >
      <ContentLayout hasSecondaryMenu secondaryActiveMenu={MenuTitles.PARTNERSHIP}>
        <FormPrompt hasUnsavedChanges={isFormDirty} />
        <AgxToast selector="#agxToast" toastState={toastState} />
        <AgxColumn gap="spacing-xl" fill>
          <AgxRow justifyContent="space-between" fill>
            <AgxTypography id={'panelAddonsHeading'} type="heading" size="x-small">
              Partnership
            </AgxTypography>
            <AgxRow gap="spacing-2xl" justifyContent="space-between" alignItems="flex-start">
              <AgxButton
                id="go-to-agency-dashboard"
                text="Go to Agency Dashboard"
                size="medium"
                variant="hollow"
                renderLink={(props) => (
                  <Link
                    to={`/${Pages.REPORTING_PAGE}/${Pages.REPORTING_PARTNERS}/${Pages.REPORTING_PARTNERS_PARTNER}/${agencyId}`}
                    {...props}
                  />
                )}
              />
              <AgxButton
                id="save-changes"
                text="Save Changes"
                dataTestId="save-changes-button"
                size="medium"
                variant="primary"
                onClick={async () => {
                  if (!agencyId) return;
                  try {
                    await updatePartnershipDetails({
                      agencyId,
                      partnershipDetails: partnershipDetailsState,
                    });
                    await invalidateAgencies();
                    await invalidateGetAgencyDetails(agencyId);
                  } catch (error) {
                    console.error('Error updating partnership or annual targets:', error);
                  }
                }}
                disabled={hasFormErrors()}
              />
            </AgxRow>
          </AgxRow>

          <AgxDivider variant="horizontal" />
          {partnershipDetailsState && (
            <AgxColumn gap="spacing-6xl" fill>
              <PartnershipInformation
                partnershipDetails={partnershipDetailsState}
                onChange={setPartnershipDetailsState}
                partnershipDefaultValue={partnershipDefaultvalue}
              />

              <PartnershipAgreement
                partnershipDetails={partnershipDetailsState}
                onChange={setPartnershipDetailsState}
                startingTierOption={startingTierOption}
                onAddTierHandler={onAddTierHandler}
                onDeleteItemHandler={onDeleteItemHandler}
              />
            </AgxColumn>
          )}
        </AgxColumn>
      </ContentLayout>
    </PageLayout>
  );
};

export default PartnershipPage;
